import type { NextPage } from "next"

import { DEFAULT_LANGUAGE, useTranslation } from "@bounce/i18n"
import { useReferrals } from "@bounce/referrals"
import type { NonNullListQueryNode } from "@bounce/util"
import { notFalsy } from "@bounce/util"

import { Hero } from "./components/Hero"
import { StoreInformation } from "./components/StoreInformation"
import { StructuredData } from "./components/StructuredData"
import { useBookNowUrl } from "./hooks/useBookNowUrl"
import { useBreadcrumbs } from "./hooks/useBreadcrumbs"
import { useInterpolations } from "./hooks/useInterpolations"
import { useReviewSlider } from "./hooks/useReviewSlider"
import { useSEO } from "./hooks/useSEO"
import { getCityCurrency } from "../../../utils/city"
import { ReviewsSlider } from "@/components/Generic/Reviews/ReviewsSlider/ReviewsSlider"
import { HowItWorksCompact } from "@/components/LuggageStorage/HowItWorks/HowItWorksCompact/HowItWorksCompact"
import { Layout } from "@/components/LuggageStorage/Layout/Layout"
import { Marquee } from "@/components/LuggageStorage/Marquee/Marquee"
import { StorePicturesSlider } from "@/components/LuggageStorage/StorePicturesSlider/StorePicturesSlider"
import { StickyCTAProvider } from "@/contexts/StickyCTAContext"
import { useStoreContact } from "@/hooks/useStoreContact"
import type { GetNearbyPoisQuery, Maybe } from "@/libs/bounce/bounceSdk.types.gen"
import type { StoreData } from "@/libs/bounce/stores/types"
import type { AggregateRating } from "@/libs/bounce/types"
import type { Simplify, StoreDocumentData } from "@/libs/prismic/types"
import { useStoreUrl } from "@/libs/router"
import useStepsCompact from "@/screens/Generic/StoreScreen/hooks/useStepsCompact"
import type { Review } from "@/utils/reviews"

type Poi = NonNullListQueryNode<GetNearbyPoisQuery["pois"]> & {
  translatedName: string
}

export type StoreScreenProps = {
  aggregateRating: AggregateRating
  pois: Poi[]
  prismicContent: Maybe<Simplify<StoreDocumentData>>
  reviews: Review[]
  store: StoreData
}

const StoreScreen: NextPage<StoreScreenProps> = ({ aggregateRating, pois, prismicContent, reviews, store }) => {
  const { t } = useTranslation("marketing-ls-store")
  const { coupon } = useReferrals.useCodes()
  const storeContact = useStoreContact({ mainStore: store, place: store.city.translatedName, city: store.city })
  const { path: bookNowPath, url: bookNowUrl } = useBookNowUrl({ store })
  const { absolutePath, getUrl: getStoreUrl } = useStoreUrl({ store })
  const interpolations = useInterpolations({ store, pois })
  const { metaTitle, metaDescription } = useSEO({ interpolations })
  const { subtitle: reviewSliderSubtitle } = useReviewSlider(reviews)
  const storeSteps = useStepsCompact({ interpolations, store })
  const breadcrumbs = useBreadcrumbs({ store, interpolations })

  const alternates = store.alternateLanguages
    .map(language => ({
      language,
      href: getStoreUrl({ language }),
    }))
    .concat([{ language: "x-default", href: getStoreUrl({ language: DEFAULT_LANGUAGE }) }])
  const pictures = store.pictures.filter(notFalsy).map(({ externalId }) => ({
    externalId,
    alt: t("screen.storeScreen.storeSlider.alt", "Storefront of Bounce Luggage Storage - {STORE_NAME}", interpolations),
  }))

  return (
    <StickyCTAProvider
      config={{
        label: t("screen.storeScreen.stickyCTA.bookNow", "Book now"),
        path: bookNowPath,
        from: "Store",
        coupon,
      }}>
      <Layout
        alternates={alternates}
        languages={store.alternateLanguages}
        breadcrumbs={breadcrumbs}
        footerProps={{ storeContact }}
        headProps={{
          title: metaTitle,
          description: metaDescription,
          url: absolutePath,
        }}
        hideNewsletterBanner
        hideDynamicAppBanner>
        <Hero
          title={t("screen.storeScreen.hero.titleV2", "Luggage storage {STORE_NAME}", interpolations)}
          store={store}
          bookNowUrl={bookNowUrl}
          interpolations={interpolations}
        />
        <Marquee aggregateRating={aggregateRating} currency={getCityCurrency(store.city)} />
        <StoreInformation
          store={store}
          about={prismicContent?.about}
          pois={pois}
          interpolations={interpolations}
          className="pt-8 sm:pt-16"
        />
        {pictures.length > 0 && (
          <StorePicturesSlider
            title={t("screen.storeScreen.storeSliderOptimized.titleV3", "See where your bags will be stored")}
            subtitle={t(
              "screen.storeScreen.storeSliderOptimized.subtitleV1",
              "Scroll through real photos from our verified Partner location",
            )}
            className="pt-section"
            pictures={pictures}
            href={bookNowUrl}
          />
        )}
        <ReviewsSlider
          className="pt-section"
          description={reviewSliderSubtitle}
          name="StoreReviews"
          reviews={reviews}
          title={t("screen.storeScreen.reviewSlider.titleV4", "What our customers say about this Partner")}
          hideReviewsModal={true}
          disableDefaultReviewsFallback={true}
        />
        <HowItWorksCompact
          steps={storeSteps}
          title={t("screen.storeScreen.howItWorks.titleV4", "Drop off in minutes, explore for hours")}
          className="pt-section"
        />
        <div className="pt-section"></div>
        <StructuredData
          store={store}
          aggregateRating={aggregateRating}
          metaDescription={metaDescription}
          reviews={reviews}
          interpolations={interpolations}
        />
      </Layout>
    </StickyCTAProvider>
  )
}

export default StoreScreen
